/**
 * Generators for the two `app/` files in every export zip.
 *
 *   app/{prefix}.js             — the "initializer": every hook, commented out
 *   app/{prefix}-overrides.css  — every core knob + component class, commented out
 *
 * Both are derived from the shipped contract (dist/docs/manifest.json,
 * ry-tokens.css, ry-theme.css), so they are complete by construction and
 * version-true: regenerate on upgrade, diff against yours, see what's new.
 *
 * Everything here is generated in the CANONICAL `ry` prefix; export.ts runs
 * the output through substitute() like every other shipped file. Pure string
 * functions — no DOM, no Node — so the same code runs in the customizer and
 * in vitest.
 */

export interface ManifestEvent { name: string; detail?: string; fires?: string; cancelable?: string }
export interface ManifestMethod { signature: string; name?: string; description?: string; static?: boolean }
export interface ManifestProperty { name: string; type?: string; description?: string; static?: boolean }
export interface ManifestComponent {
  component: string;
  /** Exported class name (`RySourceElement` for `ry-source`). Falls back to PascalCase of the tag. */
  className?: string;
  summary?: string;
  events?: ManifestEvent[];
  methods?: ManifestMethod[];
  properties?: ManifestProperty[];
}

const pascal = (tag: string): string =>
  tag.split('-').map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : '')).join('');
const classOf = (c: ManifestComponent): string => c.className ?? pascal(c.component);

const rule = (title: string): string => {
  const bar = '─'.repeat(Math.max(4, 66 - title.length));
  return `// ── ${title} ${bar}`;
};

/**
 * app/ry.js — the initializer. Structure:
 *   1. Intercept — one commented `intercept(...)` per cancelable event, per component
 *   2. Register  — static registries (manifest `static` methods) + the icon registry
 *   3. Replace   — static function slots (manifest `static` properties)
 *   4. Wire data — the adapters, as a reminder of where fetchers come from
 */
export function generateAppJs(manifest: ManifestComponent[], version: string): string {
  const out: string[] = [];
  out.push(`// app/ry.js — generated for ry-ui ${version}. THIS FILE IS YOURS.`);
  out.push(`//`);
  out.push(`// Every extension point the library exposes, commented out. Uncomment what you`);
  out.push(`// need; leave the rest. vendor/ is read-only — on upgrade you replace vendor/`);
  out.push(`// and keep this file. (A fresh export carries a regenerated copy of this file;`);
  out.push(`// diff it against yours to see hooks that appeared since.)`);
  out.push(`//`);
  out.push(`// Convention (docs/ARCHITECTURE.md → "Cancelable events"): every user-initiated`);
  out.push(`// action fires ry:before<action> BEFORE it happens — preventDefault() vetoes it`);
  out.push(`// and the ry:<action> after-event does not fire. Listen to before* to change`);
  out.push(`// what happens; listen to the plain event to react to what happened.`);
  out.push(``);
  out.push(`import {`);
  out.push(`  registerIcon, registerIcons, registerSet,`);
  out.push(`  restSource, railsSource, nestSource, postgrestSource, cursorSource,`);
  out.push(`} from '../vendor/ry-ui.js';`);
  out.push(``);
  out.push(`/** Listen for a ry-ui event fired by a specific component tag. */`);
  out.push(`export function intercept(tag, type, handler) {`);
  out.push(`  document.addEventListener(type, (e) => {`);
  out.push(`    const host = e.target;`);
  out.push(`    if (host instanceof Element && host.matches(tag)) handler(e, host);`);
  out.push(`  });`);
  out.push(`}`);
  out.push(``);

  // 1. Intercept
  out.push(rule('1. INTERCEPT — cancelable events (preventDefault() vetoes the action)'));
  out.push(``);
  const sorted = [...manifest].sort((a, b) => a.component.localeCompare(b.component));
  let interceptCount = 0;
  for (const c of sorted) {
    const cancelable = (c.events ?? []).filter((e) => e.cancelable);
    if (!cancelable.length) continue;
    out.push(`// ${c.component}`);
    for (const e of cancelable) {
      interceptCount++;
      const detail = e.detail ? `detail: ${e.detail}` : 'detail: {}';
      out.push(`//   ${e.name}  —  ${detail}`);
      if (e.fires) out.push(`//   fires: ${e.fires}`);
      out.push(`//   veto:  ${e.cancelable}`);
      out.push(`// intercept('${c.component}', '${e.name}', (e, el) => {`);
      out.push(`//   // e.preventDefault();`);
      out.push(`// });`);
    }
    out.push(``);
  }
  if (!interceptCount) out.push(`// (no cancelable events in this version's manifest)`, ``);

  // 2. Register
  out.push(rule('2. REGISTER — add to the library\'s registries'));
  out.push(``);
  out.push(`// Icons (core registry — any <ry-icon name="…"> resolves through it)`);
  out.push(`// registerIcon('logo', '<svg viewBox="0 0 24 24">…</svg>');`);
  out.push(`// registerIcons({ 'brand-a': '<svg>…</svg>', 'brand-b': '<svg>…</svg>' });`);
  out.push(`// registerSet('fomantic', { 'check': '<svg>…</svg>' }); // add/override glyphs in a set`);
  out.push(``);
  for (const c of sorted) {
    const statics = (c.methods ?? []).filter((m) => m.static);
    if (!statics.length) continue;
    const cls = classOf(c);
    out.push(`// ${c.component} — import { ${cls} } from '../vendor/ry-ui.js';`);
    for (const m of statics) {
      if (m.description) out.push(`//   ${m.signature} — ${m.description}`);
      out.push(`// ${cls}.${m.signature.replace(/\(.*$/, '')}(/* … */);`);
    }
    out.push(``);
  }

  // 3. Replace
  out.push(rule('3. REPLACE — static function slots (one global seat each)'));
  out.push(``);
  let replaceCount = 0;
  for (const c of sorted) {
    const slots = (c.properties ?? []).filter((p) => p.static);
    if (!slots.length) continue;
    const cls = classOf(c);
    out.push(`// ${c.component} — import { ${cls} } from '../vendor/ry-ui.js';`);
    for (const p of slots) {
      replaceCount++;
      if (p.description) out.push(`//   ${p.name}: ${p.type ?? ''} — ${p.description}`);
      out.push(`// ${cls}.${p.name} = /* … */;`);
    }
    out.push(``);
  }
  if (!replaceCount) out.push(`// (none declared in this version's manifest)`, ``);

  // 4. Wire data
  out.push(rule('4. WIRE DATA — give <ry-source> / [source] widgets a fetcher'));
  out.push(``);
  out.push(`// Adapters ship for REST / Rails / NestJS / PostgREST / cursor APIs`);
  out.push(`// (see docs/wiring-backends.md in the package). A fetcher is any`);
  out.push(`// (request) => Promise<{ rows, total? }>; assign it to a <ry-source>.`);
  out.push(`// document.querySelector('ry-source#users').fetcher = railsSource('/users');`);
  out.push(`// document.querySelector('ry-source#orders').fetcher = restSource('/api/orders');`);
  out.push(``);
  return out.join('\n') + '\n';
}

/**
 * app/ry-overrides.css — every token in tokens.css's `:root` block, commented
 * out with its current value, keeping the file's own section comments; then
 * one commented block per component listing its `.ry-*` theme hooks.
 *
 * The file is unlayered on purpose: vendor CSS lives in `@layer ry-tokens,
 * ry-structure, ry-theme`, so any uncommented line here wins without
 * `!important` or a specificity fight.
 */
export function generateOverridesCss(tokensCss: string, themeCss: string, version: string): string {
  const out: string[] = [];
  out.push(`/* app/ry-overrides.css — generated for ry-ui ${version}. THIS FILE IS YOURS.`);
  out.push(` *`);
  out.push(` * Every design token and component hook, commented out. Uncomment a line to`);
  out.push(` * override it. This file is NOT in a @layer, so it beats vendor/ (which is)`);
  out.push(` * with no !important. vendor/ is read-only — on upgrade you replace vendor/`);
  out.push(` * and keep this file.`);
  out.push(` *`);
  out.push(` * Tokens: the CORE KNOBS section is the theme — ~18 values everything else`);
  out.push(` * derives from. Start there. (Your customizer choices already live in`);
  out.push(` * ../ry-brand.css; this file is for hand overrides on top.)`);
  out.push(` */`);
  out.push(``);
  out.push(`:root {`);
  out.push(...commentOutRootBlock(tokensCss).map((l) => (l ? `  ${l}` : l)));
  out.push(`}`);
  out.push(``);
  out.push(`/* ═══════════════════════════════════════════════════════════════`);
  out.push(`   COMPONENT HOOKS — every selector vendor/css/ry-theme.css styles.`);
  out.push(`   Hosts are styled by tag (ry-button[variant="primary"]), generated`);
  out.push(`   inner markup by class (.ry-btn__label). One block per root.`);
  out.push(`   ═══════════════════════════════════════════════════════════════ */`);
  out.push(``);
  for (const [, { root, hooks }] of themeHooks(themeCss)) {
    const others = hooks.filter((h) => h !== root);
    out.push(`/* ${root}${others.length ? `: ${others.join('  ')}` : ''} */`);
    out.push(`/* ${root} { } */`);
    out.push(``);
  }
  return out.join('\n') + '\n';
}

/** Lines of the first top-level `:root { … }` block in tokens.css, with every
 *  declaration commented out (inline comments hoisted outside the wrapper so
 *  CSS comments don't nest). Section comments pass through untouched. */
function commentOutRootBlock(css: string): string[] {
  const start = css.indexOf(':root {');
  if (start < 0) return ['/* (no :root block found in tokens.css) */'];
  // Find the matching close brace, honoring nesting.
  let depth = 0; let i = css.indexOf('{', start); let end = -1;
  for (; i < css.length; i++) {
    const ch = css[i];
    if (ch === '{') depth++;
    else if (ch === '}') { depth--; if (depth === 0) { end = i; break; } }
  }
  if (end < 0) return ['/* (unterminated :root block) */'];
  const body = css.slice(css.indexOf('{', start) + 1, end);

  const out: string[] = [];
  const lines = body.split('\n');
  let pending = '';      // accumulating a multi-line declaration
  let inComment = false; // inside a multi-line /* … */ section comment
  for (const raw of lines) {
    const line = raw.replace(/^ {2}/, ''); // drop the :root indent
    const t = line.trim();
    if (inComment) { out.push(line); if (t.includes('*/')) inComment = false; continue; }
    if (!pending) {
      if (t === '') { out.push(''); continue; }
      if (t.startsWith('/*')) { out.push(line); if (!t.includes('*/')) inComment = true; continue; }
      if (t.startsWith('color-scheme')) { out.push(`/* ${t} */`); continue; }
    }
    pending = pending ? `${pending} ${t}` : t;
    if (!/;\s*(\/\*.*\*\/\s*)?$/.test(pending) && !pending.endsWith(';')) continue; // still open
    // Split off a trailing inline comment so we don't nest comments.
    const m = pending.match(/^(.*?;)\s*(\/\*.*\*\/)?\s*$/);
    const decl = (m?.[1] ?? pending).trim();
    const note = m?.[2] ? ` ${m[2]}` : '';
    out.push(`/* ${decl.replace(/\*\//g, '* /')} */${note}`);
    pending = '';
  }
  // Trim leading/trailing blank lines
  while (out.length && out[0] === '') out.shift();
  while (out.length && out[out.length - 1] === '') out.pop();
  return out;
}

/** Every theme hook in theme.css, grouped by component:
 *    ry-button  → ['ry-button', 'ry-button[variant="primary"]', …]   (host tag selectors)
 *    ry-btn     → ['.ry-btn', '.ry-btn--primary', …]                  (BEM classes)
 *  theme.css styles hosts by TAG (`ry-button[variant]`) and generated inner
 *  markup by CLASS (`.ry-btn__label`), so both are hooks. */
function themeHooks(css: string): Map<string, { root: string; hooks: string[] }> {
  // Keep selector text only: drop comments, at-rule preludes, and leaf bodies.
  const text = css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/@(layer|keyframes|property|supports|container|media)[^{;]*/g, '')
    .replace(/\{[^{}]*\}/g, '{}');
  const groups = new Map<string, { root: string; hooks: Set<string> }>();
  const add = (base: string, root: string, hook: string) => {
    if (!groups.has(base)) groups.set(base, { root, hooks: new Set() });
    groups.get(base)!.hooks.add(hook);
  };
  // Host tag selectors, with their attribute qualifiers: ry-button, ry-button[variant="primary"]
  for (const m of text.matchAll(/(?<![\w.#-])(ry-[a-z0-9]+(?:-[a-z0-9]+)*)((?:\[[^\]]*\])*)/g)) {
    add(m[1]!, m[1]!, `${m[1]}${m[2] ?? ''}`);
  }
  // BEM classes: .ry-btn, .ry-btn__label, .ry-btn--primary
  for (const m of text.matchAll(/\.(ry-[a-z0-9]+(?:-[a-z0-9]+)*(?:__[a-z0-9-]+)?(?:--[a-z0-9-]+)?)/g)) {
    const base = m[1]!.split('__')[0]!.split('--')[0]!;
    add(`.${base}`, `.${base}`, `.${m[1]}`);
  }
  const sorted = new Map<string, { root: string; hooks: string[] }>();
  for (const base of [...groups.keys()].sort((a, b) => a.replace(/^\./, '').localeCompare(b.replace(/^\./, '')) || a.localeCompare(b))) {
    const g = groups.get(base)!;
    sorted.set(base, { root: g.root, hooks: [...g.hooks].sort() });
  }
  return sorted;
}
